// Where a worktree stands, which is what the Work page groups by and what an
// agent is told (convex/agent.ts). Kept here so that the two say the same.

/**
 * In the order the page shows them. Each is a different thing to do next:
 * open a pull request, commit, wait for review, remove. The last two are the
 * ones Echoes can say least about, so they come last.
 */
export const WORK_STATES = [
  "ready",
  "uncommitted",
  "inReview",
  "merged",
  "unlinked",
  "defaultBranch",
] as const;
export type WorkState = (typeof WORK_STATES)[number];

type Standing = {
  pullRequest: { state: string } | null;
  branch: string | null;
  base: string | null;
  projectId: string | null;
  ahead: number;
};

export function stateOf(work: Standing): WorkState {
  if (work.pullRequest?.state === "open") return "inReview";
  if (work.pullRequest?.state === "merged") return "merged";
  if (work.branch === null || work.branch === work.base) return "defaultBranch";
  // Echoes only knows the pull requests of a project's repositories: for
  // any other it cannot say there is none.
  if (work.projectId === null) return "unlinked";
  return work.ahead > 0 ? "ready" : "uncommitted";
}

const STALE_AFTER_MS = 14 * 24 * 60 * 60_000;

/** Left alone long enough that it is probably forgotten. */
export function isStale(work: { touchedAt: number | null; firstSeenAt: number }, now: number): boolean {
  return now - (work.touchedAt ?? work.firstSeenAt) >= STALE_AFTER_MS;
}

/** The kinds of line the journal writes: see `workEventKindValidator` in convex/workTables.ts. */
export const WORK_EVENT_KINDS = ["started", "committed", "edited", "pushed", "removed"] as const;
